// OnBehalfModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { X } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import AddNewUserForm from './AddNewUserForm';
import AddExistingUserForm from './AddExistingUserForm';
import useOrderApi from '../../hooks/useOrderApi';

const OnBehalfModal = ({ isOpen, onClose, onUserSelected, brandId }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [activeTab, setActiveTab] = useState('existing');
  const [countries, setCountries] = useState([]);
  const { getCountries } = useOrderApi();
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    
    const loadCountries = async () => {
      if (!isOpen) return;
      
      try {
        const data = await getCountries();
        if (isMounted && isMountedRef.current) {
          setCountries(data);
        }
      } catch (error) {
        console.error('Failed to load countries:', error);
      }
    };
    
    if (isOpen) {
      loadCountries();
    }
    
    return () => {
      isMounted = false;
    };
  }, [isOpen, getCountries]);

  // Reset tab when modal closes
  useEffect(() => {
    if (!isOpen) {
      setActiveTab('existing');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleUserCreated = (user) => {
    onUserSelected(user);
  };

  const handleUserSelected = (user) => {
    onUserSelected(user);
  };

  const overlayClassName = clsx(
    'fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4'
  );

  const modalClassName = clsx(
    'relative w-full max-w-2xl rounded-lg shadow-xl max-h-[90vh] overflow-y-auto',
    {
      'bg-slate-800': isDark,
      'bg-white': !isDark
    }
  );

  const headerClassName = clsx(
    'sticky top-0 z-10 px-6 py-4 border-b flex items-center justify-between',
    {
      'bg-slate-800 border-slate-700': isDark,
      'bg-white border-gray-200': !isDark
    }
  );

  const titleClassName = clsx(
    'text-xl font-semibold',
    {
      'text-slate-200': isDark,
      'text-gray-900': !isDark
    }
  );

  const tabButtonClassName = (isActive) => clsx(
    'flex-1 py-3 px-4 font-medium transition-colors border-b-2',
    {
      'border-blue-500 text-blue-600': isActive && !isDark,
      'border-blue-400 text-blue-400': isActive && isDark,
      'border-transparent hover:border-gray-300': !isActive && !isDark,
      'border-transparent hover:border-slate-600': !isActive && isDark,
      'text-gray-600': !isActive && !isDark,
      'text-slate-400': !isActive && isDark
    }
  );

  return (
    <div className={overlayClassName} onClick={onClose}>
      <div className={modalClassName} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={headerClassName}>
          <h2 className={titleClassName}>Add user to order</h2>
          <button
            onClick={onClose}
            className={clsx(
              'p-1 rounded-lg transition-colors',
              {
                'hover:bg-slate-700 text-slate-400': isDark,
                'hover:bg-gray-100 text-gray-500': !isDark
              }
            )}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className={clsx(
          'flex border-b',
          {
            'border-slate-700': isDark,
            'border-gray-200': !isDark
          }
        )}>
          <button
            onClick={() => setActiveTab('new')}
            className={tabButtonClassName(activeTab === 'new')}
          >
            Add new user
          </button>
          <button
            onClick={() => setActiveTab('existing')}
            className={tabButtonClassName(activeTab === 'existing')}
          >
            Add existing user
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {activeTab === 'new' ? (
            <AddNewUserForm
              brandId={brandId}
              onUserCreated={handleUserCreated}
              onCancel={onClose}
              countries={countries}
            />
          ) : (
            <AddExistingUserForm
              onUserSelected={handleUserSelected}
              onCancel={onClose}
            />
          )}
        </div>
      </div>
    </div>
  );
};

OnBehalfModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUserSelected: PropTypes.func.isRequired,
  brandId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};

export default OnBehalfModal;